// app_client/src/components/common/Sidebar.jsx
import { NavLink, useLocation } from 'react-router-dom';
import { useState } from 'react';
import useAuthStore from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { 
  LayoutDashboard,
  Users,
  UserCheck,
  ShieldUser,
  ChevronLeft, 
  ChevronRight,
  ChevronDown,
  LogOut,
  FileStack,
  Briefcase,
  Box,
  ShieldAlert,
  Truck,
  Wallet,
  Settings,
  History,
  FolderLock,
  Building2,
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout, hasPermission } = useAuthStore();
  const { isSidebarCollapsed, toggleSidebar } = useUIStore();
  const location = useLocation();

  // Hesap Yönetimi altındaki sayfaların aktiflik kontrolü
  const isAccountGroupActive = (
    location.pathname.startsWith('/financial-accounts') ||
    location.pathname.startsWith('/transactions')
  );

  const isSystemCenterActive = location.pathname.startsWith('/admin/system');

  // Alt grup menüsünün açık/kapalı state'i
  const [isAccountGroupOpen, setIsAccountGroupOpen] = useState(() => isAccountGroupActive);
  const [isSystemCenterOpen, setIsSystemCenterOpen] = useState(() => isSystemCenterActive);

  // MÎMÂRÎ KAIZEN: Sıralamayı ve grupları tek diziden yöneten konfigürasyon yapısı
  const menuItems = [
    { 
      id: 'dash', 
      type: 'item', 
      name: 'Dashboard', 
      icon: <LayoutDashboard size={20} />, 
      path: '/', 
      perm: null 
    },
    { 
      id: 'account', 
      type: 'item', 
      name: 'Müşteri Yönetimi', 
      icon: <Briefcase size={20} />, 
      path: '/accounts', 
      perm: 'account'
    },
    { 
      id: 'operation', 
      type: 'item', 
      name: 'Operasyon Yönetimi', 
      icon: <Truck size={20} />, 
      path: '/operations', 
      perm: null
    },
    {
      id: 'personnel',
      type: 'item',
      name: 'Personel Yönetimi',
      icon: <UserCheck size={20} />,
      path: '/personnel',
      perm: 'account'
    },
    {
      id: 'financial_group',
      type: 'group',
      name: 'Hesap Yönetimi',
      icon: <Wallet size={20} />,
      perm: 'financialaccounts', // Ana grup görme yetki kontrolü baz alınır
      isOpen: isAccountGroupOpen,
      setOpen: setIsAccountGroupOpen,
      isActive: isAccountGroupActive,
      children: [
        { id: 'acc_trans', name: 'Hesap Hareketleri', path: '/transactions', perm: 'transaction' },
        { id: 'acc_list', name: 'Hesap Listesi', path: '/financial-accounts', perm: 'financialaccounts' }
      ]
    },
    { 
      id: 'invoice', 
      type: 'item', 
      name: 'Faturalar', 
      icon: <FileStack size={20} />, 
      path: '/invoices', 
      perm: 'invoice' 
    },
    { 
      id: 'product', 
      type: 'item', 
      name: 'Ürün & Hizmet', 
      icon: <Box size={20} />, 
      path: '/products', 
      perm: 'product' 
    },
    {
      id: 'system_center',
      type: 'group',
      name: 'Sistem Merkezi',
      icon: <ShieldAlert size={20} />,
      isOpen: isSystemCenterOpen,
      setOpen: setIsSystemCenterOpen,
      isActive: isSystemCenterActive,
      children: [
        { id: 'system_settings', name: 'Genel Ayarlar', icon: <Settings size={16} />, path: '/admin/system/settings', perm: 'system' },
        { id: 'system_company', name: 'Şirket Bilgileri', icon: <Building2 size={16} />, path: '/admin/system/company-info', perm: 'system' },
        { id: 'system_documents', name: 'Evrak Tanımları', icon: <FolderLock size={16} />, path: '/admin/system/documents', perm: 'documents' },
        { id: 'system_audit_logs', name: 'Log Kayıtları', icon: <History size={16} />, path: '/admin/system/audit-logs', perm: 'system', allowedRoles: ['admin'] },
        { id: 'system_users', name: 'Kullanıcı Yönetimi', icon: <Users size={16} />, path: '/admin/system/users', perm: 'users' },
        { id: 'system_roles', name: 'Rol Yönetimi', icon: <ShieldUser size={16} />, path: '/admin/system/roles', perm: 'roles', allowedRoles: ['admin'] },
        { id: 'system_modules', name: 'Modül Yetkileri', icon: <ShieldAlert size={16} className="text-amber-500" />, path: '/admin/system/modules', perm: 'modulemanagement' }
      ]
    }
  ];

  const normalizeRole = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  const roleName = normalizeRole(user?.roleName);
  const isPrivilegedRole = roleName === 'admin' || roleName === 'superadmin';

  // CSS Sınıf Üretici Fabrikası (Kurumsal ERP Teması)
  const linkClass = (isActive, isSubmenu = false) => `
    flex items-center rounded-xl transition-all duration-200 group relative border-l-4
    ${isSubmenu ? 'py-2.5 pr-3 pl-4' : 'p-3'}
    ${isActive
      ? 'border-blue-600 bg-blue-600/10 text-blue-300 shadow-[inset_0_0_18px_rgba(37,99,235,0.12)]'
      : 'border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-white'}
  `;

  return (
    <aside className={`bg-slate-950 text-white h-screen transition-all duration-300 flex flex-col shadow-2xl ${isSidebarCollapsed ? 'w-20' : 'w-64'}`}>
      
      {/* Kurumsal Logo Alanı */}
      <div className="p-5 flex items-center justify-between border-b border-slate-800/50">
        {!isSidebarCollapsed && (
          <div className="flex flex-col">
            <span className="font-black text-xl tracking-tighter text-blue-500 uppercase">Doğançay</span>
            <span className="text-[10px] text-slate-500 font-bold tracking-[0.2em] -mt-1">NAKLİYAT</span>
          </div>
        )}
        <button 
          onClick={toggleSidebar}
          className="p-1.5 hover:bg-slate-800 rounded-xl transition-all border border-transparent hover:border-slate-700"
        >
          {isSidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Navigasyon Linkleri Komuta Merkezi */}
      <nav className="flex-1 mt-6 px-3 space-y-1.5 overflow-y-auto custom-scrollbar">
        {menuItems.map((item) => {
          // GÜVENLİK KALKANI: Eğer item grubu ise veya tekil ise izinlerini kontrol et
          if (item.type === 'group') {
            // Grubun altındaki çocuklardan en az birini görmeye yetkisi var mı?
            const canSeeAnyChild = item.children.some(child => {
              const roleAllowed = isPrivilegedRole || !child.allowedRoles || child.allowedRoles.map(normalizeRole).includes(roleName);
              const permissionAllowed = !child.perm || hasPermission(child.perm, 'read');
              return roleAllowed && permissionAllowed;
            });
            if (!canSeeAnyChild) return null;

            return (
              <div key={item.id} className="w-full">
                <button
                  type="button"
                  onClick={() => item.setOpen((current) => !current)}
                  className={`
                    w-full flex items-center p-3 rounded-xl transition-all duration-200 group border-l-4
                    ${item.isActive
                      ? 'border-blue-600 bg-blue-600/10 text-blue-300 shadow-[inset_0_0_18px_rgba(37,99,235,0.12)]'
                      : 'border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-white'}
                  `}
                >
                  <div className={`transition-transform duration-200 group-hover:scale-110 ${isSidebarCollapsed ? 'mx-auto' : ''}`}>
                    {item.icon}
                  </div>
                  {!isSidebarCollapsed && (
                    <>
                      <span className="ml-3 font-semibold text-sm tracking-wide flex-1 text-left">{item.name}</span>
                      <ChevronDown size={16} className={`transition-transform ${item.isOpen ? 'rotate-180' : ''}`} />
                    </>
                  )}
                </button>

                {/* Alt Menü Elemanları (Collapse Açık ve Sidebar Genişken) */}
                {!isSidebarCollapsed && item.isOpen && (
                  <div className="mt-1 ml-5 pl-3 border-l border-slate-800 space-y-1">
                    {item.children.map((child) => {
                      const roleAllowed = isPrivilegedRole || !child.allowedRoles || child.allowedRoles.map(normalizeRole).includes(roleName);
                      const canSeeChild = roleAllowed && (!child.perm || hasPermission(child.perm, 'read'));
                      if (!canSeeChild) return null;

                      return (
                        <NavLink
                          key={child.id}
                          to={child.path}
                          className={({ isActive }) => linkClass(isActive, true)}
                        >
                          {child.icon && <span className="mr-2 shrink-0">{child.icon}</span>}
                          <span className="font-semibold text-xs tracking-wide">{child.name}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // TEKİL LINK RENDERING (Grup Olmayan Düz Menü Öğeleri)
          const canSee = !item.perm || hasPermission(item.perm, 'read');
          if (!canSee) return null;

          return (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) => linkClass(isActive)}
            >
              <div className={`transition-transform duration-200 group-hover:scale-110 ${isSidebarCollapsed ? 'mx-auto' : ''}`}>
                {item.icon}
              </div>
              {!isSidebarCollapsed && <span className="ml-3 font-semibold text-sm tracking-wide">{item.name}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Alt Kısım: Kullanıcı ve Çıkış Kilidi */}
      <div className="p-4 border-t border-slate-800/50 bg-slate-900/30">
        {!isSidebarCollapsed && (
          <div className="mb-4 px-2">
            <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest">Oturum</p>
            <p className="text-sm font-bold text-slate-200 truncate">{user?.name || 'Hasan Kerem'}</p>
            <p className="text-[11px] text-blue-500 font-medium">{isPrivilegedRole ? 'Sistem Yöneticisi' : 'Personel'}</p>
          </div>
        )}
        <button 
          onClick={logout}
          className="flex items-center w-full p-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-all group"
        >
          <LogOut size={20} className="group-hover:-translate-x-1 transition-transform" />
          {!isSidebarCollapsed && <span className="ml-3 font-bold text-sm tracking-wide">Güvenli Çıkış</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
